<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3'
import { CheckCircle, FileText, Phone, Search, SlidersHorizontal, X } from 'lucide-vue-next'
import { computed, onUnmounted, ref, watch } from 'vue'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppPagination from '@/components/AppPagination.vue'
import AppTooltip from '@/components/AppTooltip.vue'
import RelativeTime from '@/components/RelativeTime.vue'
import CallTracker from '@/components/applications/CallTracker.vue'
import StatusBadge from '@/components/applications/StatusBadge.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import AppLayout from '@/layouts/AppLayout.vue'
import { APPLICATION_STATUSES } from '@/lib/constants'
import { formatAmount, formatDate, formatLabel } from '@/lib/utils'
import { editLoanApplicationPath, loanApplicationsPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'

interface Application {
  id: string
  status: string
  status_at: string | null
  rejection_reason: string | null
  loan_type: string
  amount: number
  call_count: number
  customer_name: string
  customer_email: string
  customer_phone: string
  partner_name: string
  purpose: string
  created_at: string
  updated_at: string
  completed_at: string | null
  state: string | null
  has_uploaded_document: boolean
  has_pinned_comment: boolean
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  status?: string | null
  purpose?: string | null
  date_from?: string | null
  date_to?: string | null
  sort?: string | null
  direction?: string | null
  per_page?: string | null
}

const props = defineProps<{
  applications: Application[]
  pagination: PaginationMeta
  filters: Filters
  purposes: string[]
}>()

const breadcrumbs: BreadcrumbItem[] = [{ title: 'Loan Applications', href: loanApplicationsPath() }]

const ALL = '__all__'

const search = ref(props.filters.q ?? '')
const statusFilter = ref(props.filters.status ?? ALL)
const purposeFilter = ref(props.filters.purpose ?? ALL)
const dateFrom = ref(props.filters.date_from ?? '')
const dateTo = ref(props.filters.date_to ?? '')
const sortCol = ref(props.filters.sort ?? '')
const sortDir = ref<'asc' | 'desc'>((props.filters.direction as 'asc' | 'desc') ?? 'desc')
const perPage = ref(props.filters.per_page ?? '20')

const STATUS_DATE_LABELS: Record<string, string> = {
  new: 'Created',
  taken: 'Taken',
  accepted: 'Accepted',
  approved: 'Approved',
  settled: 'Settled',
  rejected: 'Rejected',
}

const dateColLabel = computed(() => (statusFilter.value !== ALL ? STATUS_DATE_LABELS[statusFilter.value] : 'Created'))

const today = new Date().toISOString().split('T')[0]

const hasActiveFilters = computed(
  () =>
    search.value !== '' ||
    statusFilter.value !== ALL ||
    purposeFilter.value !== ALL ||
    dateFrom.value !== '' ||
    dateTo.value !== '',
)

const filterParams = computed(() => ({
  q: search.value || undefined,
  status: statusFilter.value !== ALL ? statusFilter.value : undefined,
  purpose: purposeFilter.value !== ALL ? purposeFilter.value : undefined,
  date_from: dateFrom.value || undefined,
  date_to: dateTo.value || undefined,
  sort: sortCol.value || undefined,
  direction: sortCol.value ? sortDir.value : undefined,
  per_page: perPage.value !== '20' ? perPage.value : undefined,
}))

function applyFilters() {
  const params = Object.fromEntries(Object.entries(filterParams.value).filter(([, v]) => v != null))
  router.get(loanApplicationsPath(), params, { preserveState: true, replace: true })
}

let searchTimer: ReturnType<typeof setTimeout>

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})

watch([statusFilter, purposeFilter, dateFrom, dateTo], applyFilters)

onUnmounted(() => clearTimeout(searchTimer))

function clearFilters() {
  search.value = ''
  statusFilter.value = ALL
  purposeFilter.value = ALL
  dateFrom.value = ''
  dateTo.value = ''
  sortCol.value = ''
  sortDir.value = 'desc'
  perPage.value = '20'
}

function changePerPage(value: string) {
  perPage.value = value
  applyFilters()
}

function handleSort(key: string, dir: 'asc' | 'desc') {
  sortCol.value = key
  sortDir.value = dir
  applyFilters()
}

function isStale(date: string): boolean {
  return Date.now() - new Date(date).getTime() >= 24 * 60 * 60 * 1000
}

const tableColumns = computed<TableColumn[]>(() => [
  { key: 'customer_name', label: 'Name', sortable: true },
  { key: 'created_at', label: 'Created', sortable: true },
  { key: 'updated_at', label: 'Updated', sortable: true },
  { key: 'phone', label: 'Phone' },
  { key: 'type_purpose', label: 'Purpose / Partner' },
  { key: 'status', label: 'Status' },
  { key: 'amount', label: 'Amount' },
  { key: 'actions', label: '', align: 'right' },
])
</script>

<template>
  <Head title="Loan Applications" />
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 overflow-x-auto p-4">
      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader>
          <div class="flex items-center justify-between">
            <div>
              <CardTitle class="text-base">All Applications</CardTitle>
              <CardDescription>
                {{ pagination.total_count.toLocaleString() }} application{{ pagination.total_count !== 1 ? 's' : '' }}
                {{ hasActiveFilters ? 'found' : 'total' }}
              </CardDescription>
            </div>
            <SlidersHorizontal class="text-muted-foreground size-4" />
          </div>
        </CardHeader>

        <!-- Search & filters -->
        <div class="border-border border-b px-4 pb-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <!-- Search -->
            <div class="relative min-w-48 flex-1">
              <Search
                class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
              />
              <Input v-model="search" placeholder="Search by customer, email or partner…" class="pl-9" />
            </div>

            <!-- Status filter -->
            <Select v-model="statusFilter">
              <SelectTrigger class="w-full sm:w-40">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem :value="ALL">Any status</SelectItem>
                <SelectItem v-for="s in APPLICATION_STATUSES" :key="s" :value="s">{{ formatLabel(s) }}</SelectItem>
              </SelectContent>
            </Select>

            <!-- Purpose filter -->
            <Select v-model="purposeFilter">
              <SelectTrigger class="w-full sm:w-48">
                <SelectValue placeholder="Any purpose" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem :value="ALL">Any purpose</SelectItem>
                <SelectItem v-for="p in props.purposes" :key="p" :value="p">{{ formatLabel(p) }}</SelectItem>
              </SelectContent>
            </Select>

            <!-- Date range filter -->
            <div class="flex w-full flex-col items-center gap-1.5 sm:flex-row md:w-120">
              <AppDatePicker
                v-model="dateFrom"
                :max="dateTo || today"
                :placeholder="`${dateColLabel} from`"
                class="w-full sm:w-48"
              />
              <span class="text-muted-foreground hidden text-xs select-none md:block">–</span>
              <AppDatePicker
                v-model="dateTo"
                :min="dateFrom || undefined"
                :max="today"
                :placeholder="`${dateColLabel} to`"
                class="w-full sm:w-48"
              />
            </div>

            <!-- Clear filters -->
            <Button
              v-if="hasActiveFilters"
              variant="ghost"
              size="sm"
              class="text-muted-foreground hover:text-foreground shrink-0"
              @click="clearFilters"
            >
              <X class="size-4" />
              Clear
            </Button>
          </div>
        </div>

        <CardContent class="p-0">
          <!-- Empty state -->
          <div v-if="applications.length === 0" class="flex flex-col items-center gap-3 py-16 text-center">
            <FileText class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">
              {{ hasActiveFilters ? 'No applications match your filters' : 'No applications yet' }}
            </p>
            <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
              <X class="size-4" />
              Clear filters
            </Button>
          </div>

          <!-- Table -->
          <AppDataTable
            v-else
            :columns="tableColumns"
            :sort="sortCol || undefined"
            :direction="sortDir"
            @sort="handleSort"
          >
            <tr
              v-for="app in applications"
              :key="app.id"
              class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
              :class="{ 'bg-amber-50/60 dark:bg-amber-900/10': app.status === 'new' && isStale(app.updated_at) }"
            >
              <!-- Name -->
              <td class="px-6 py-4">
                <div class="flex items-center gap-2">
                  <AppTooltip v-if="app.has_pinned_comment" content="Has pinned note" side="top">
                    <span class="relative flex size-2 shrink-0 cursor-default">
                      <span class="absolute -inset-1.5 animate-ping rounded-full bg-red-400 opacity-75 animation-duration-[1.5s]" />
                      <span class="relative inline-flex size-2 rounded-full bg-red-500" />
                    </span>
                  </AppTooltip>
                  <div>
                    <a :href="editLoanApplicationPath(app.id)" target="_blank" class="font-medium hover:underline">{{ app.customer_name }}</a>
                    <span class="text-muted-foreground mt-0.5 block text-xs">{{ app.customer_email }}</span>
                  </div>
                </div>
              </td>
              <!-- Created -->
              <td class="px-6 py-4">
                <RelativeTime :date="app.created_at" />
                <div v-if="app.completed_at" class="mt-0.5 flex items-center gap-1">
                  <CheckCircle class="text-primary size-3.5 shrink-0" />
                  <AppTooltip :content="`Completed: ${formatDate(app.completed_at)}`" side="top">
                    <span class="text-muted-foreground cursor-default border-b border-dotted text-xs">
                      <RelativeTime :date="app.completed_at" />
                    </span>
                  </AppTooltip>
                </div>
              </td>
              <!-- Updated -->
              <td class="px-6 py-4">
                <RelativeTime :date="app.updated_at" />
              </td>
              <!-- Phone + State -->
              <td class="px-6 py-4">
                <div class="flex flex-wrap items-center gap-1">
                  <a
                    :href="`tel:${app.customer_phone}`"
                    class="text-muted-foreground flex items-center gap-1 text-sm hover:underline"
                  >
                    <Phone class="size-3 shrink-0" />
                    {{ app.customer_phone }}
                  </a>
                  <span
                    v-if="app.state"
                    class="rounded-full border ml-1 border-pink-700 bg-pink-200 px-1.5 py-0.5 text-xs text-pink-700"
                  >{{ app.state }}</span>
                </div>
              </td>
              <!-- Purpose / Partner -->
              <td class="px-6 py-4">
                <span class="text-foreground text-sm font-medium">{{ formatLabel(app.purpose) }}</span>
                <span class="text-muted-foreground mt-0.5 block text-xs">{{ app.partner_name }}</span>
              </td>
              <!-- Status + Call + Has Doc -->
              <td class="px-6 py-4">
                <div class="flex items-center gap-3">
                  <StatusBadge
                    :status="app.status"
                    :status-at="app.status_at"
                    :rejection-reason="app.rejection_reason"
                  />
                  <CallTracker
                    v-if="app.status === 'new'"
                    :application-id="app.id"
                    :call-count="app.call_count"
                    :show-counter="false"
                    :return-to="loanApplicationsPath()"
                  />
                  <AppTooltip v-if="app.has_uploaded_document" content="Has uploaded documents" side="top">
                    <FileText class="size-4 shrink-0 text-red-400" />
                  </AppTooltip>
                </div>
              </td>
              <!-- Amount -->
              <td class="text-muted-foreground px-6 py-4">{{ formatAmount(app.amount) }}</td>
              <!-- Actions -->
              <td class="px-6 py-4 text-right">
                <a
                  :href="editLoanApplicationPath(app.id)"
                  target="_blank"
                  class="text-primary shrink-0 text-xs hover:underline"
                >
                  Open →
                </a>
              </td>
            </tr>
          </AppDataTable>

          <!-- Pagination + per-page -->
          <div class="flex items-center justify-between px-2 py-3">
            <AppPagination
              v-if="pagination.total_pages > 1"
              :current-page="pagination.current_page"
              :total-pages="pagination.total_pages"
              :total-count="pagination.total_count"
              :per-page="pagination.per_page"
              :base-url="loanApplicationsPath()"
              :extra-params="filterParams"
            />
            <div class="ml-auto flex items-center gap-2">
              <span class="text-muted-foreground text-xs">Per page</span>
              <Select :model-value="perPage" @update:model-value="(v) => v && changePerPage(String(v))">
                <SelectTrigger class="h-8 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  <SelectItem v-for="n in ['10', '20', '100', '250']" :key="n" :value="n" class="text-xs">{{ n }}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
